import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { MailMergeResult, OutputType } from './mailmergeExport';

interface MailMergeResultsTableProps {
  results: Map<string, MailMergeResult>;
  outputType: OutputType;
}

const MailMergeResultsTable: React.FC<MailMergeResultsTableProps> = ({ results, outputType }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const entries = Array.from(results.values());

  return (
    <ScrollArea className="h-[350px] rounded-md border border-border/60">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Lead</TableHead>
            <TableHead>Email</TableHead>
            {(outputType === 'email' || outputType === 'both') && (
              <TableHead>Subject</TableHead>
            )}
            {(outputType === 'call' || outputType === 'both') && (
              <TableHead>Call Script</TableHead>
            )}
            <TableHead className="w-24">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((r) => (
            <React.Fragment key={r.leadId}>
              <TableRow
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => setExpandedId(expandedId === r.leadId ? null : r.leadId)}
              >
                <TableCell className="p-2">
                  {expandedId === r.leadId ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </TableCell>
                <TableCell className="font-medium truncate max-w-[120px]">{r.leadName}</TableCell>
                <TableCell className="text-sm text-muted-foreground truncate max-w-[140px]">
                  {r.leadEmail || '—'}
                </TableCell>
                {(outputType === 'email' || outputType === 'both') && (
                  <TableCell className="text-sm truncate max-w-[160px]">
                    {r.subject || '—'}
                  </TableCell>
                )}
                {(outputType === 'call' || outputType === 'both') && (
                  <TableCell className="text-sm truncate max-w-[120px]">
                    {r.callScript ? `${r.callScript.substring(0, 40)}…` : '—'}
                  </TableCell>
                )}
                <TableCell>
                  <StatusBadge status={r.status} />
                </TableCell>
              </TableRow>
              {expandedId === r.leadId && (
                <TableRow>
                  <TableCell colSpan={10} className="bg-muted/20 p-4">
                    <div className="space-y-3 text-sm">
                      {r.status === 'failed' && r.error && (
                        <div className="text-destructive font-medium">Error: {r.error}</div>
                      )}
                      {r.subject && (
                        <div>
                          <div className="font-semibold text-muted-foreground mb-1">Subject</div>
                          <div>{r.subject}</div>
                        </div>
                      )}
                      {r.emailBody && (
                        <div>
                          <div className="font-semibold text-muted-foreground mb-1">Email Body</div>
                          <pre className="whitespace-pre-wrap font-sans text-sm bg-white text-gray-900 p-3 rounded-md border border-border/60 max-h-[200px] overflow-y-auto">
                            {r.emailBody}
                          </pre>
                        </div>
                      )}
                      {r.callScript && (
                        <div>
                          <div className="font-semibold text-muted-foreground mb-1">Call Script</div>
                          <pre className="whitespace-pre-wrap font-sans text-sm bg-background p-3 rounded-md border border-border/60 max-h-[200px] overflow-y-auto">
                            {r.callScript}
                          </pre>
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </ScrollArea>
  );
};

function StatusBadge({ status }: { status: MailMergeResult['status'] }) {
  switch (status) {
    case 'success':
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/30">Done</Badge>;
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>;
    case 'generating':
      return <Badge variant="secondary">Running</Badge>;
    default:
      return <Badge variant="outline">Pending</Badge>;
  }
}

export default MailMergeResultsTable;
